// AWS Cognito configuration utilities
import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import { CognitoConfig } from '../types/auth';

// Validate that required environment variables are present
const validateEnvVars = (): void => {
  const requiredVars = ['REACT_APP_USERPOOL_ID', 'REACT_APP_APPCLIENT_ID'];
  const missingVars = requiredVars.filter(varName => !process.env[varName]);
  
  if (missingVars.length > 0) {
    console.warn(
      `Missing required environment variables: ${missingVars.join(', ')}. ` +
      'Please check your .env file and ensure all AWS Cognito credentials are configured.'
    );
  }
};

// Initialize validation check
validateEnvVars();

// Export Cognito configuration
export const cognitoConfig: CognitoConfig = {
  UserPoolId: process.env.REACT_APP_USERPOOL_ID || 'your_user_pool_id_here',
  ClientId: process.env.REACT_APP_APPCLIENT_ID || 'your_app_client_id_here',
  region: process.env.REACT_APP_AWS_REGION || 'us-east-1'
};

// Create and export the User Pool instance
export const userPool = new CognitoUserPool({
  UserPoolId: cognitoConfig.UserPoolId,
  ClientId: cognitoConfig.ClientId
});

// Debug logging for configuration (only in development)
if (process.env.NODE_ENV === 'development') {
  console.log('Cognito Configuration:', {
    UserPoolId: cognitoConfig.UserPoolId,
    ClientId: cognitoConfig.ClientId,
    Region: cognitoConfig.region
  });
}

// Utility function to create a CognitoUser instance
export const createCognitoUser = (email: string): CognitoUser => {
  return new CognitoUser({
    Username: email,
    Pool: userPool
  });
};

// Utility function to create AuthenticationDetails
export const createAuthenticationDetails = (email: string, password: string): AuthenticationDetails => {
  return new AuthenticationDetails({
    Username: email,
    Password: password
  });
};

// Utility function to get current authenticated user
export const getCurrentUser = (): CognitoUser | null => {
  return userPool.getCurrentUser();
};

// Utility function to validate configuration
export const validateCognitoConfig = (): boolean => {
  const hasUserPoolId = cognitoConfig.UserPoolId && cognitoConfig.UserPoolId !== 'your_user_pool_id_here';
  const hasClientId = cognitoConfig.ClientId && cognitoConfig.ClientId !== 'your_app_client_id_here';
  
  if (!hasUserPoolId || !hasClientId) {
    console.warn('AWS Cognito configuration is using placeholder values. Please update your .env file with actual AWS Cognito credentials.');
    return false;
  }
  
  return true;
};

// Export individual config values for convenience
export const {
  UserPoolId,
  ClientId,
  region
} = cognitoConfig;